/**
 * @file index.tsx
 *
 * @fileoverview Shows the add link for multi widgets like education or work experience.
 */

import React, { useContext } from 'react';
import Link from '~/jobiqo/components/Link';
import { UserContext, UserContextInterface } from '~/jobiqo/context/user';
import { OrganizationProfile } from '~/jobiqo/types/types';
import { getCommonTranslations } from '~/jobiqo/utils/intl';
import { Button } from '~/vcl/atoms/01-forms/01-button';
import { HiddenVisually as VisuallyHidden } from '~/vcl/atoms/14-w-a11y/02-visually-hidden';
import { EditIcon } from '~/vcl/icons/Edit';
import { cva } from 'class-variance-authority';
import styles from './editLink.module.scss';

export interface EditLinkProps {
  /**
   * The current box id.
   */
  boxId: string | undefined;
  /**
   * For multi level items the index.
   */
  fieldIndex?: number;
  /**
   * The current user resume.
   */
  organization: OrganizationProfile;
  /**
   * The editing entity id.
   */
  entityId?: string;
  /**
   * If the box has a title the edit button should align with it.
   *
   * @default false
   */
  boxHasTitle?: boolean;
}

const linkClass = cva('', {
  variants: {
    boxHasTitle: {
      true: styles.link,
      false: '',
    },
  },
});

export const EditLink = ({
  boxId,
  fieldIndex,
  organization,
  entityId,
  boxHasTitle = false,
}: EditLinkProps) => {
  const userContext: UserContextInterface = useContext(UserContext);
  const constructLink = (id) =>
    typeof fieldIndex !== 'undefined' ? `${boxId}/${id}` : boxId;
  const { EDIT } = getCommonTranslations();

  return userContext.user && organization.canEdit ? (
    <Link
      href={`/organization/edit/${organization.id}/${constructLink(
        entityId ?? fieldIndex,
      )}`}
      scroll={false}
      data-testid={`jbq-organization-edit-${constructLink(fieldIndex)}`}
      className={linkClass({ boxHasTitle })}
    >
      {
        // Since we cannot use context in end2end tests we are not able
        // to reference the entity id so we have to use fieldIndex which
        // represents the order number of rendered UI elements.
      }
      <Button
        buttonStyle="grayLight"
        fab
        className={`${styles.btn} jd-org-edit-link-btn`}
      >
        <VisuallyHidden>{EDIT}</VisuallyHidden>
        <EditIcon title={EDIT} />
      </Button>
    </Link>
  ) : null;
};
